import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { OfflineDataDb } from '@app/core/db/offline-data';
import { DocumentViewerCacheService } from '@app/core/services/document-viewer-cache.service';
import {
  getEventTotalBytes,
  sizeExceededMessage,
  sizeExceedsLimit,
} from '@app/work-order/documents/viewer-size-guard.util';

@Component({
  selector: 'app-video-viewer',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './video-viewer.component.html',
  styleUrls: ['./video-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VideoViewerComponent implements OnInit, OnChanges, OnDestroy {
  /** When set, the viewer auto-loads the video from this URL. */
  @Input() src: string | null = null;
  /** Display name shown in the toolbar. */
  @Input() externalFileName: string | null = null;
  @Input() knownFileType: string | null = null;
  @Input() knownFileSize: number | null = null;

  isLoading = false;
  loadError: string | null = null;
  sizeExceeded = false;
  loadProgress: number | null = null;
  videoSrc: string | null = null;

  private currentObjectUrl: string | null = null;
  private loadSubscription: Subscription | null = null;

  constructor(
    private httpClient: HttpClient,
    private offlineData: OfflineDataDb,
    private viewerCache: DocumentViewerCacheService,
    private cdr: ChangeDetectorRef,
  ) {}

  get fileName(): string | null {
    return this.externalFileName ?? this.src?.split('/').pop() ?? null;
  }

  private get extension(): string {
    return (this.knownFileType || this.fileName?.split('.').pop() || 'mp4').toLowerCase();
  }

  ngOnInit(): void {
    if (this.src) {
      void this.loadVideo(this.src);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['src'] && !changes['src'].firstChange) {
      const url = changes['src'].currentValue as string | null;
      if (url) {
        void this.loadVideo(url);
      } else {
        this.revokeCurrentUrl();
        this.videoSrc = null;
      }
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.revokeCurrentUrl();
  }

  private async loadVideo(url: string): Promise<void> {
    const extension = this.extension;
    if (this.knownFileSize && sizeExceedsLimit(extension, this.knownFileSize)) {
      this.handleSizeExceeded(extension, this.knownFileSize);
      return;
    }
    this.isLoading = true;
    this.loadError = null;
    this.sizeExceeded = false;
    this.loadProgress = null;
    this.videoSrc = null;
    this.cdr.markForCheck();

    const cached = this.viewerCache.get(url);
    if (cached instanceof Blob) {
      this.setObjectUrl(cached);
      return;
    }

    // Try offline storage next (matches WorkOrderDocumentsComponent.loadPdf pattern)
    const offlineFile = await this.offlineData.workOrderFiles
      .where('url')
      .equals(url)
      .first()
      .catch(() => null);

    if (offlineFile) {
      const blob = new Blob([offlineFile.data], offlineFile.type ? { type: offlineFile.type } : undefined);
      if (sizeExceedsLimit(extension, blob.size)) {
        this.handleSizeExceeded(extension, blob.size);
        return;
      }
      this.setObjectUrl(blob);
      return;
    }

    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.httpClient
      .get(url, { responseType: 'blob', observe: 'events', reportProgress: true })
      .subscribe({
        next: (event) => {
          const totalBytes = getEventTotalBytes(event);
          if (totalBytes && sizeExceedsLimit(extension, totalBytes)) {
            this.loadSubscription?.unsubscribe();
            this.handleSizeExceeded(extension, totalBytes);
            return;
          }
          if (event.type === HttpEventType.DownloadProgress && event.total) {
            this.loadProgress = Math.round((event.loaded / event.total) * 100);
            this.cdr.markForCheck();
          } else if (event.type === HttpEventType.Response && event.body) {
            this.viewerCache.set(url, event.body);
            this.setObjectUrl(event.body);
          }
        },
        error: (err) => {
          console.warn('Failed to load video from network and no offline copy found', err);
          this.loadError = `Failed to load video: ${err.message ?? err.status}`;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private handleSizeExceeded(extension: string, totalBytes: number): void {
    this.isLoading = false;
    this.sizeExceeded = true;
    this.loadProgress = null;
    this.loadError = sizeExceededMessage(extension, totalBytes);
    this.cdr.markForCheck();
  }

  private setObjectUrl(blob: Blob): void {
    this.revokeCurrentUrl();
    this.currentObjectUrl = URL.createObjectURL(blob);
    this.videoSrc = this.currentObjectUrl;
    this.isLoading = false;
    this.loadProgress = null;
    this.cdr.markForCheck();
  }

  private revokeCurrentUrl(): void {
    if (this.currentObjectUrl) {
      URL.revokeObjectURL(this.currentObjectUrl);
      this.currentObjectUrl = null;
    }
  }
}
